<template>
  <div>
    <v-card class="mb-3">
      <v-card-title>
        <h1>Vessel Particulars</h1>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <h2 class="mb-4">Details</h2>
        <v-row dense>
          <v-col cols="12" md="6">
            <text-input v-model="vessel.name" label="Name"></text-input>
          </v-col>
          <v-col cols="12" md="6">
            <text-input v-model="vessel.systemType" label="Type"></text-input>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="6">
            <text-input
              v-model="vessel.dpClass"
              :rules="[rules.integer]"
              label="DP class"
            ></text-input>
          </v-col>
          <v-col cols="12" md="6">
            <text-input
              v-model="vessel.vesselDesign"
              label="V/L design"
            ></text-input>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="6">
            <text-input v-model="vessel.builtBy" label="Built by"></text-input>
          </v-col>
          <v-col cols="12" md="6">
            <text-input
              v-model="vessel.builtYear"
              :rules="[rules.integer]"
              label="Delivered"
            ></text-input>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="6">
            <text-input
              v-model="vessel.classificationSociety"
              label="Classification society"
            ></text-input>
          </v-col>
          <v-col cols="12" md="6">
            <text-input
              v-model="vessel.classNotation"
              label="Class notation"
            ></text-input>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="6">
            <text-input v-model="vessel.client" label="Client"></text-input>
          </v-col>
        </v-row>

        <h2 class="my-4">Principal dimensions</h2>
        <v-row dense>
          <v-col cols="12" md="6">
            <text-input
              v-model="vessel.overallLength"
              label="Length overall"
              required
              :rules="[rules.decimal]"
            ></text-input>
          </v-col>
          <v-col cols="12" md="6">
            <text-input
              v-model="vessel.breadth"
              label="Breadth"
              :rules="[rules.decimal]"
            ></text-input>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="6">
            <text-input
              v-model="vessel.mainDeckDepth"
              label="Depth main deck"
              :rules="[rules.decimal]"
            ></text-input>
          </v-col>
          <v-col cols="12" md="6">
            <text-input
              v-model="vessel.draftMidships"
              label="Draft midships"
              :rules="[rules.decimal]"
            ></text-input>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="6">
            <text-input
              v-model="vessel.grossTonnage"
              label="Gross tonnage"
              :rules="[rules.decimal]"
            ></text-input>
          </v-col>
          <v-col cols="12" md="6">
            <text-input
              v-model="vessel.netTonnage"
              :rules="[rules.decimal]"
              label="Net tonnage"
            ></text-input>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="6">
            <text-input :rules="[rules.decimal]" v-model="vessel.dwt" label="DWT"></text-input>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="mb-3">
      <v-card-title>
        <h1>Machinery details</h1>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <h2 class="mb-4">Power</h2>
        <v-row dense>
          <v-col cols="12" md="6">
            <text-input
              v-model="vessel.powerGenType"
              label="Power generation type"
            ></text-input>
          </v-col>
          <v-col cols="12" md="6">
            <text-input v-model="vessel.engines" label="Engines"></text-input>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="6">
            <text-input
              v-model="vessel.generators"
              label="Generators"
            ></text-input>
          </v-col>
        </v-row>
        <h2 class="my-4">Thrusters</h2>
        <v-row dense>
          <v-col cols="12" md="6">
            <text-input
              v-model="vessel.thrusterControlSystem"
              label="Thruster control system"
            ></text-input>
          </v-col>
          <v-col cols="12" md="6">
            <text-input
              v-model="vessel.managementSystem"
              label="Vessel management system"
            ></text-input>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" md="6">
            <text-input
              v-model="vessel.joystickDescription"
              label="Independent joystick description"
            ></text-input>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card class="mb-3">
      <v-card-title>
        <h1>DP System</h1>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <h2 class="mb-4">Details</h2>
        <v-row dense>
          <v-col cols="12" md="6">
            <text-input
              v-model="vessel.dpMake"
              label="Supplied by"
            ></text-input>
          </v-col>
          <v-col cols="12" md="6">
            <text-input
              v-model="vessel.dpSituated"
              label="Situated"
            ></text-input>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
    <v-card class="mb-3">
      <v-card-title>
        <h1>Reference and sensor systems</h1>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <edit-table
          :headers="systemHeaders"
          :items="systems"
          item-title="system"
          @add-item="postSystem"
          @delete-item="deleteSystem"
        >
          <v-row dense>
            <v-col cols="12" md="6">
              <text-input
                v-model="system.number"
                label="Number"
                required
                :rules="[rules.required]"
              ></text-input>
            </v-col>
            <v-col cols="12" md="6">
              <text-input
                v-model="system.make"
                label="Make"
                required
                :rules="[rules.required]"
              ></text-input>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12" md="6">
              <text-input
                v-model="system.model"
                label="Model"
                required
                :rules="[rules.required]"
              ></text-input>
            </v-col>
          </v-row>
        </edit-table>
      </v-card-text>
    </v-card>
    <v-card class="mb-3">
      <v-card-title>
        <h1>Switchboard configurations</h1>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <edit-table
          :headers="boardConfigHeaders"
          :items="boardConfigs"
          item-title="configuration"
          @add-item="postConfig"
          @delete-item="deleteConfig"
        >
          <v-row dense>
            <v-col cols="12" md="12">
              <text-input
                v-model="boardConfig.configuration"
                label="Configuration"
                required
                :rules="[rules.required]"
              ></text-input>
            </v-col>
          </v-row>
        </edit-table>
      </v-card-text>
    </v-card>
    <v-card class="mb-3">
      <v-card-title>
        <h1>Thrusters</h1>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text>
        <edit-table
          :headers="thrusterHeaders"
          :items="thrusters"
          item-title="thruster"
          @add-item="postThruster"
          @delete-item="deleteThruster"
        >
          <v-row dense>
            <v-col cols="12" md="6">
              <text-input
                v-model="thruster.number"
                label="Number"
                required
                :rules="[rules.required]"
              ></text-input>
            </v-col>
            <v-col cols="12" md="6">
              <text-input
                v-model="thruster.make"
                label="Make"
                required
                :rules="[rules.required]"
              ></text-input>
            </v-col>
          </v-row>
          <v-row dense>
            <v-col cols="12" md="6">
              <text-input
                v-model="thruster.model"
                label="Model"
                required
                :rules="[rules.required]"
              ></text-input>
            </v-col>
            <v-col cols="12" md="6">
              <text-input
                v-model="thruster.type"
                label="Type"
                required
                :rules="[rules.required]"
              ></text-input>
            </v-col>
          </v-row>
        </edit-table>
      </v-card-text>
    </v-card>
    <v-btn color="primary" @click="putVessel" width="100%" :loading="loading"
      >Save</v-btn
    >
  </div>
</template>

<script>
import ApiService from '@/services/ApiService.js'
import Objects from '@/services/Objects.js'
import TextInput from '@/components/TextInput.vue'
import EditTable from '@/components/tables/EditTable.vue'
import Validation from '@/mixins/validation.js'

export default {
  name: 'VesselDetails',
  components: {
    TextInput,
    EditTable
  },
  mixins: [Validation],
  props: {
    id: String
  },
  data() {
    return {
      vessel: '',
      system: {
        vesselId: this.id,
        number: '',
        make: '',
        model: ''
      },
      systemHeaders: [
        { text: 'Number', value: 'number' },
        { text: 'Make', value: 'make' },
        { text: 'Model', value: 'model' },
        { value: 'action', width: '10' }
      ],
      systems: [],
      boardConfig: {
        vesselId: this.id,
        configuration: ''
      },
      boardConfigHeaders: [
        { text: 'Configuration', value: 'configuration' },
        { value: 'action', width: '10' }
      ],
      boardConfigs: [],
      thruster: {
        vesselId: this.id,
        number: '',
        make: '',
        model: '',
        type: ''
      },
      thrusterHeaders: [
        { text: 'Number', value: 'number' },
        { text: 'Make', value: 'make' },
        { text: 'Model', value: 'model' },
        { text: 'Type', value: 'type' },
        { value: 'action', width: '10' }
      ],
      thrusters: [],
      loading: false
    }
  },
  created() {
    this.getVessel(), this.getSystems(), this.getConfigs(), this.getThrusters()
  },
  methods: {
    getVessel() {
      this.loading = true
      ApiService.get(Objects.Vessel, this.id)
        .then(response => {
          this.vessel = response.data
        })
        .finally(() => {
          this.loading = false
        })
    },
    putVessel() {
      this.loading = true
      ApiService.put(Objects.Vessel, this.id, this.vessel).then(() => {
        this.getVessel()
      })
    },
    deleteSystem(item) {
      ApiService.delete(
        Objects.VesselReferenceSensor,
        item.vesselReferenceSensorId
      ).then(() => {
        this.getSystems()
      })
    },
    getSystems() {
      ApiService.get(Objects.VesselReferenceSensor + '/byvessel', this.id).then(
        response => {
          this.systems = response.data
        }
      )
    },
    postSystem() {
      ApiService.post(Objects.VesselReferenceSensor, this.system).then(() => {
        this.getSystems()
        this.system.number = ''
        this.system.make = ''
        this.system.model = ''
      })
    },
    getConfigs() {
      ApiService.get(
        Objects.VesselSwitchboardConfig + '/byvessel',
        this.id
      ).then(response => {
        this.boardConfigs = response.data
      })
    },
    postConfig() {
      ApiService.post(Objects.VesselSwitchboardConfig, this.boardConfig).then(
        () => {
          this.getConfigs()
          this.boardConfig.configuration = ''
        }
      )
    },
    deleteConfig(item) {
      ApiService.delete(
        Objects.VesselSwitchboardConfig,
        item.vesselSwitchboardConfigId
      ).then(() => {
        this.getConfigs()
      })
    },
    getThrusters() {
      ApiService.get(Objects.VesselThruster + '/byvessel', this.id).then(
        response => {
          this.thrusters = response.data
        }
      )
    },
    postThruster() {
      ApiService.post(Objects.VesselThruster, this.thruster).then(() => {
        this.getThrusters()
        this.thruster.number = ''
        this.thruster.make = ''
        this.thruster.model = ''
        this.thruster.type = ''
      })
    },
    deleteThruster(item) {
      ApiService.delete(Objects.VesselThruster, item.VesselThrusterId).then(
        () => {
          this.getThrusters()
        }
      )
    }
  }
}
</script>

<style scoped></style>
